import "./PlaceEquityOrderWizard.scss";
import template from "./PlaceEquityOrderWizard.html";
import {AdviceTypes} from "../place-order-wizard/models/AdviceTypes";
import {IOnDestroy, IOnInit, IScope} from "angular";
import {EquityWizardData} from "./models/EquityWizardData";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {CurrentUserService} from "../../../../services/user/CurrentUserService";
import {AccountStore} from "../../../../account/store/AccountStore";
import {AccountSource} from "../../../../account/types/AccountSource";
import {ReadonlyAccountStore} from "../../../../account/store/ReadonlyAccountStore";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {PortfolioService} from "../../../service/PortfolioService";
import {PortfolioType} from "../../../models/Portfolio";
import {isNotNull} from "../../../../js/util/UtilFunctions";
import {AccountBriefRecord} from "../../../../account/types/AccountBriefRecord";
import {OrderAdviceService} from "../services/OrderAdviceService";
import {DuplicateOrderService} from "../services/DuplicateOrderService";
import {DuplicateOrderSummaries} from "../types/DuplicateOrdersVettingResponse";
import {DuplicateOrdersNotificationDialog} from "../place-order-wizard/dialog/DuplicateOrdersNotificationDialog";
import {EquityOrderWizardStore} from "./store/EquityOrderWizardStore";
import {QuoteStore} from "../../../../quote/store/QuoteStore";
import {buildOrderRequest} from "./util/EquityWizardOrderUtil";
import {defaultAdvice, isSameAdvice, OrderAdvice} from "../place-order-wizard/models/OrderAdvice";
import {ObservableWithLazyLatest} from "../../../../js/classes/lazy-observable/ObservableWithLazyLatest";
import {UserConfig} from "../../../../types/UserConfig";

interface Resolve {
    readonly order: EquityWizardData;
}

export const PlaceEquityWizard = "tfEquityOrderWizard";

export function placeEquityWizard() {
    return {
        template,
        bindings: {
            modalInstance: "<",
            resolve: "<"
        },
        controller: PlaceEquityOrderWizardController,
        controllerAs: "ctrl"
    };
}

export class PlaceEquityOrderWizardController implements IOnInit, IOnDestroy {

    /** @input */
    public readonly resolve: Resolve;

    /** @output */
    public accountBalance: number;

    /** @input */
    public readonly modalInstance: IModalInstanceService;

    public readonly hasAdvice$: Observable<boolean>;
    public readonly orderAdviceAccess$: Observable<boolean>;
    public readonly currentAccountBrief$: Observable<AccountBriefRecord>;
    public readonly adviceTypes$: Observable<AdviceTypes>;
    public readonly processingOrder$ = new BehaviorSubject(false);
    public readonly advice$ = new ReplaySubject<OrderAdvice>(1);

    public readonly accountSource: AccountSource;
    public store: EquityOrderWizardStore;
    public isParentOrder$: Observable<boolean | null>;

    private readonly isAdviser$: Observable<boolean>;
    private readonly placeOrder$ = new Subject<boolean>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    private quoteStore: QuoteStore;

    /** @ngInject */
    public constructor(private readonly $scope: IScope,
                       private readonly currentUserService: CurrentUserService,
                       private readonly portfolioService: PortfolioService,
                       private readonly orderAdviceService: OrderAdviceService,
                       private readonly duplicateOrderService: DuplicateOrderService,
                       private readonly duplicateOrdersNotificationDialog: DuplicateOrdersNotificationDialog,
                       private readonly userConfig: UserConfig,
                       quoteStore: QuoteStore,
                       accountStore: AccountStore) {
        this.quoteStore = quoteStore;
        this.accountSource = ReadonlyAccountStore.create(accountStore);
        this.isAdviser$ = this.buildIsAdviser();
        this.hasAdvice$ = this.buildHasAdvice();
        this.orderAdviceAccess$ = this.buildOrderAdviceAccess$();
        this.currentAccountBrief$ = this.buildCurrentAccountBrief();
        this.adviceTypes$ = this.buildAdviceTypes();
    }

    private buildHasAdvice(): Observable<boolean> {
        const source$ = Observable.combineLatest(this.accountSource.current, this.isAdviser$);
        return source$.filter(([portfolio]) => isNotNull(portfolio))
                      .map(([portfolio, isAdviser]) => isAdviser && (portfolio?.portfolioType === PortfolioType.BROKER))
                      .publishReplay(1)
                      .refCount();
    }

    private buildIsParentOrder(): Observable<boolean | null> {
        return this.store
                   .current
                   .takeUntil(this.destroyed$)
                   .map((equityData) => equityData.isParentOrder)
                   .publishReplay(1)
                   .refCount();
    }

    private buildOrderAdviceAccess$(): Observable<boolean> {
        return this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((user) => user.hasOrderAdviceAccess)
            .publishReplay(1)
            .refCount();
    }

    private buildIsAdviser(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async)
                   .map((user) => user.isAdviser())
                   .publishReplay(1)
                   .refCount();
    }

    private buildCurrentAccountBrief(): Observable<AccountBriefRecord> {
        return this.accountSource
                   .current
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .publishReplay(1)
                   .refCount();
    }

    private buildAdviceTypes(): Observable<AdviceTypes> {
        return this.accountSource
                   .current
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .filter((a) => a.portfolioType === PortfolioType.BROKER)
                   .map((a) => a.id)
                   .distinctUntilChanged()
                   .switchMap((id) => this.fetchAdviceTypes(id))
                   .publishReplay(1)
                   .refCount();
    }

    private fetchAdviceTypes(portfolioId: number): Observable<AdviceTypes> {
        return Observable.of(portfolioId)
                         .flatMap((id) => this.orderAdviceService.get(id))
                         .catch((err) => Observable.empty<AdviceTypes>());
    }

    public async $onInit() {
        this.store = new EquityOrderWizardStore(this.quoteStore, this.accountSource, this.resolve.order);
        this.watchAdviceTypes();
        this.watchAdviceChange();
        this.watchAccountSelection();
        this.watchPlaceOrder();
        this.isParentOrder$ = this.buildIsParentOrder();
    }

    private watchAdviceTypes() {
        const $source = ObservableWithLazyLatest.create(this.adviceTypes$, this.currentUserService.current)
                                                .map(([types, user]) => defaultAdvice(types, user));

        this.hasAdvice$
            .distinctUntilChanged()
            .takeUntil(this.destroyed$)
            .switchMap((hasAdvice) => hasAdvice ? $source : Observable.of(null))
            .subscribe((advice) => this.store.updateAdvice(advice));
    }

    private watchAdviceChange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((o) => o.advice)
            .filter(isNotNull)
            .distinctUntilChanged(isSameAdvice)
            .map((adv) => Object.assign({}, adv)) // Need to copy due to ng-model bindings sucking
            .subscribe((adv) => this.advice$.next(adv));
    }

    private watchAccountSelection() {
        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .subscribe((portfolio) => this.store.updatePortfolio(portfolio));
    }

    private watchPlaceOrder() {
        this.placeOrder$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.processingOrder$)
            .filter(([_, processing]) => !processing)
            .do(() => this.processingOrder$.next(true))
            .withLatestFrom(this.store.current)
            .map(([_, order]) => order)
            .withLatestFrom(this.accountSource.current)
            .switchMap(([data, account]) => this.validateRequest(data, account))
            .subscribe((data) => this.modalInstance.close(data));
    }

    /* Empty if the request should not be submitted, otherwise return the request to submit */
    private validateRequest(data: EquityWizardData,
                            account: AccountBriefRecord | null): Observable<EquityWizardData> {
        if (account == null) {
            this.processingOrder$.next(false);
            return Observable.empty();
        }
        const request = buildOrderRequest(data);
        const result = this.duplicateOrderService.validate(account.id, [request]);
        return Observable.fromPromise(result)
                         .flatMap((res) => this.proceedIfDuplicates(res.duplicates))
                         .do((proceed) => console.debug("Order should proceed?", proceed))
                         .do(() => this.processingOrder$.next(false))
                         .filter((proceed) => proceed)
                         .do(() => console.debug("Order proceeded"))
                         .map(() => data);
    }

    private proceedIfDuplicates(duplicates: DuplicateOrderSummaries): Observable<boolean> {
        if (duplicates.length === 0) {
            return Observable.of(true);
        } else {
            return this.notifyDuplicates(duplicates);
        }
    }

    private notifyDuplicates(duplicates: DuplicateOrderSummaries): Observable<boolean> {
        return this.duplicateOrdersNotificationDialog.open(duplicates);
    }

    public adviceChanged(advice: OrderAdvice) {
        this.store.updateAdvice(advice);
    }

    public accountBalanceChanged(accountBalance: number) {
        this.accountBalance = accountBalance;
    }

    public placeOrder() {
        this.placeOrder$.next(true);
    }

    public cancel() {
        this.modalInstance.dismiss();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
        this.store.destroy();
    }
}
